// Necessary if using App Router to ensure this file runs on the client
"use client";

import { datadogRum } from "@datadog/browser-rum";
import { useSession } from "next-auth/react";
import { useEffect } from "react";

import { useNextEnvVars } from "@/components/context-providers/NextEnvVarsWrapper";
import { rumBeforeSend } from "@/utils/datadog-rum-filter";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { useIsImpersonating } from "@/utils/hooks/use-is-impersonating";

const DOPPLER_ENV_TO_ENV: Record<string, string> = {
  prd: "prod",
  stg: "staging",
};

let GLOBAL_HAS_DATADOG_RUM_BEEN_INITIALIZED: boolean = false;

// `sessionReplaySampleRate` samples replays for ordinary sessions; `force` records this one
// regardless. Recording starts at the call, so call it as early in the funnel as possible.
function forceRumSessionReplay() {
  datadogRum.startSessionReplayRecording({ force: true });
}

// Records every visit to the page that calls it, so the whole self-signup funnel has a replay.
export function useForceRumReplay() {
  useEffect(() => {
    forceRumSessionReplay();
  }, []);
}

export function useRumIdentity() {
  const session = useSession();
  const { team, environment, isServerlessEnvironment } = useRouteAndTeamInfo();
  const { CHALK_DEPLOYMENT_MODEL, CHALK_PLATFORM_VERSION } = useNextEnvVars();
  const isChalkEmployee = useDANGEROUSisChalkEmployee({ ignoreAdminMode: true });
  const isImpersonating = useIsImpersonating();
  const homeTeamId = session.data?.teamId ?? null;
  const userEmail = session.data?.user?.email ?? null;
  const userName = session.data?.user?.name ?? null;

  // Self-service teams get a serverless environment. Employees and impersonated sessions are
  // not customers, so they keep the ordinary sampling.
  const shouldForceReplay =
    team != null &&
    isServerlessEnvironment === true &&
    !isChalkEmployee &&
    isImpersonating === false;
  useEffect(() => {
    if (shouldForceReplay) {
      forceRumSessionReplay();
    }
  }, [shouldForceReplay]);

  useEffect(() => {
    if (session.data?.userId) {
      // `setUser` replaces the whole user, so an omitted field is cleared. Absent values are
      // left out rather than set to undefined: RUM stringifies any `id`/`name`/`email` key that
      // is present, so `email: undefined` would be recorded as "undefined". `team_*` is the
      // viewed team, which differs from `home_team_id` while impersonating.
      datadogRum.setUser({
        id: session.data.userId,
        ...(userEmail != null && { email: userEmail }),
        ...(userName != null && { name: userName }),
        ...(team != null && { team_id: team.id, team_name: team.name }),
        ...(homeTeamId != null && { home_team_id: homeTeamId }),
      });
    } else {
      datadogRum.clearUser();
    }

    datadogRum.setGlobalContextProperty("is_chalk_employee", isChalkEmployee);

    // Left unset while undecided (an employee whose viewed team has not resolved), so
    // `@context.is_impersonating:false` matches only sessions known to be real customer usage.
    if (isImpersonating != null) {
      datadogRum.setGlobalContextProperty("is_impersonating", isImpersonating);
    } else {
      datadogRum.removeGlobalContextProperty("is_impersonating");
    }

    if (CHALK_DEPLOYMENT_MODEL != null) {
      datadogRum.setGlobalContextProperty("deployment_model", CHALK_DEPLOYMENT_MODEL);
    } else {
      datadogRum.removeGlobalContextProperty("deployment_model");
    }

    if (CHALK_PLATFORM_VERSION != null) {
      datadogRum.setGlobalContextProperty("platform_version", CHALK_PLATFORM_VERSION);
    } else {
      datadogRum.removeGlobalContextProperty("platform_version");
    }

    if (environment != null) {
      datadogRum.setGlobalContextProperty("is_serverless", isServerlessEnvironment ?? false);
    } else {
      datadogRum.removeGlobalContextProperty("is_serverless");
    }
  }, [
    CHALK_DEPLOYMENT_MODEL,
    CHALK_PLATFORM_VERSION,
    environment,
    homeTeamId,
    isChalkEmployee,
    isImpersonating,
    isServerlessEnvironment,
    session.data?.userId,
    team,
    userEmail,
    userName,
  ]);
}

export interface DatadogInitProps {
  dopplerEnvironment: string | undefined;
}

function shouldInitializeDatadogRum(): boolean {
  if (typeof window === "undefined") {
    return true;
  }
  return window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1";
}

export function initGlobalDatadogRum(dopplerEnvironment: string | undefined) {
  datadogRum.init({
    applicationId: "685d251a-b6e8-41ac-83e1-9f40ec0237f5",
    clientToken: "pub2893c276a2d9b8641e0d714ca2c13317",
    site: "datadoghq.com",
    service: "frontend",
    env: DOPPLER_ENV_TO_ENV[dopplerEnvironment || ""] || "development",
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    beforeSend: rumBeforeSend,
    proxy: "/api/ddrum", // Proxy to handle RUM requests
    // Specify URLs to propagate trace headers for connection between RUM and backend trace
    allowedTracingUrls: [
      { match: "https://api.chalk.ai", propagatorTypes: ["tracecontext"] },
      { match: "https://api.staging.chalk.ai", propagatorTypes: ["tracecontext"] },
      { match: "https://api.staging.chalk.dev", propagatorTypes: ["tracecontext"] },
    ],
  });

  datadogRum.setGlobalContextProperty("frontend_host", window.location.host);
}

export default function DatadogInit(props: DatadogInitProps) {
  // Render nothing - this component is only included so that the init code
  // above will run client-side
  useEffect(() => {
    if (!GLOBAL_HAS_DATADOG_RUM_BEEN_INITIALIZED && shouldInitializeDatadogRum()) {
      initGlobalDatadogRum(props.dopplerEnvironment);
      GLOBAL_HAS_DATADOG_RUM_BEEN_INITIALIZED = true;
    }
  });
  return null;
}
